import {
	MAP_COLORS,
	DEFAULT_PLACEMARK_ITEM_STYLES,
	SELECTED_PLACEMARK_ITEM_STYLES
} from '../../util/MapUtil.jsx';
import { placemarkDisplayState } from '../../util/kmlMap.js';

export const DIMMED_PLACEMARK_ITEM_STYLES = {
	polygon: {
		strokeColor: '#9aa0a6',
		strokeWeight: 1,
		strokeOpacity: 0.45,
		fillColor: '#9aa0a6',
		fillOpacity: 0.12
	},
	polyline: {
		strokeColor: '#9aa0a6',
		strokeWeight: 4,
		strokeOpacity: 0.45
	},
	marker: {
		opacity: 0.45
	}
};

export function applyDimmedPlacemarkStyles(placemarkItem) {
	const styles = DIMMED_PLACEMARK_ITEM_STYLES[placemarkItem.type] || DIMMED_PLACEMARK_ITEM_STYLES.marker;
	placemarkItem.setOptions(styles);
}

/**
 * Imperative Google Maps placemark mutations — call from effects / event handlers only,
 * never from React render.
 */

/**
 * Apply filter-driven visibility and colors to geoXML3 placemark overlays.
 * @returns {Object} visibleColorsHashmap for MapLegend
 */
export function applyPlacemarkFilterStyles(placemarkItems, {
	projectMapGroupBy,
	filteredActivitiesMap,
	activityCodeType
}) {
	const visibleColorsHashmap = {};
	const hasActivityCodeType = Boolean(activityCodeType);

	Object.keys(placemarkItems).forEach(placemarkItemKey => {
		const placemarkItem = placemarkItems[placemarkItemKey];
		const display = placemarkDisplayState({
			activities: placemarkItem.activities,
			projectMapGroupBy,
			filteredActivitiesMap,
			hasActivityCodeType
		});
		if (display === 'dimmed') {
			placemarkItem.setVisible(true);
			applyDimmedPlacemarkStyles(placemarkItem);
			return;
		}
		if (display === 'hidden') {
			placemarkItem.setVisible(false);
			return;
		}
		if (display === 'shown') {
			placemarkItem.setVisible(true);
			applyDefaultPlacemarkStyles(placemarkItem);
			return;
		}
		const filteredPlacemarkActivities = placemarkItem.activities.filter(
			activity => activity.activityId in filteredActivitiesMap
				&& `_${projectMapGroupBy}` in activity.properties
		);

		if (filteredPlacemarkActivities.length) {
			placemarkItem.setVisible(true);

			const earliestActivity = filteredPlacemarkActivities.reduce((accumulator, activity) => {
				return activity.start < accumulator.start ? activity : accumulator;
			});

			if (`_${projectMapGroupBy}` in earliestActivity.properties) {
				const colorIndex = activityCodeType.valuesIndex[
					earliestActivity.properties[`_${projectMapGroupBy}`].id
				];
				const color = MAP_COLORS[colorIndex];
				visibleColorsHashmap[colorIndex] = true;
				if (placemarkItem.type === 'polyline') {
					placemarkItem.setOptions({
						strokeColor: color,
						opacity: 0.8
					});
				} else {
					placemarkItem.setOptions({
						fillColor: color,
						opacity: 1
					});
				}
			} else {
				placemarkItem.setVisible(false);
			}
		} else {
			placemarkItem.setVisible(true);
			applyDimmedPlacemarkStyles(placemarkItem);
		}
	});

	return visibleColorsHashmap;
}

/**
 * No legend group selected: keep every pin on the map.
 * Pins with no matched activities are dimmed.
 */
export function applyUngroupedPlacemarkStyles(placemarkItems) {
	Object.keys(placemarkItems).forEach(placemarkItemKey => {
		const placemarkItem = placemarkItems[placemarkItemKey];
		const display = placemarkDisplayState({
			activities: placemarkItem.activities,
			hasActivityCodeType: false
		});
		placemarkItem.setVisible(true);
		if (display === 'dimmed') {
			applyDimmedPlacemarkStyles(placemarkItem);
		} else {
			applyDefaultPlacemarkStyles(placemarkItem);
		}
	});
}

/**
 * Highlight the selected placemark; reset others to default stroke styles.
 */
export function applyPlacemarkSelectionStyles(placemarkItems, selectedTitle) {
	Object.keys(placemarkItems).forEach(placemarkItemKey => {
		const placemarkItem = placemarkItems[placemarkItemKey];
		placemarkItem.setOptions(
			placemarkItem.title === selectedTitle
				? SELECTED_PLACEMARK_ITEM_STYLES[placemarkItem.type]
				: DEFAULT_PLACEMARK_ITEM_STYLES[placemarkItem.type]
		);
	});
}

export function applyDefaultPlacemarkStyles(placemarkItem) {
	placemarkItem.setOptions(DEFAULT_PLACEMARK_ITEM_STYLES[placemarkItem.type]);
}

/**
 * Pulse stroke weight on polygon/polyline selection.
 * @param {Object} placemarkItem
 * @param {{ current: number|null }} pulseIntervalRef
 */
export function startStrokePulse(placemarkItem, pulseIntervalRef) {
	clearInterval(pulseIntervalRef.current);
	pulseIntervalRef.current = null;

	if (placemarkItem.type !== 'polygon' && placemarkItem.type !== 'polyline') {
		return;
	}

	const weightMin = 8;
	const weightMax = 16;
	const step = 0.5;
	let direction = 1;
	let weight = 1;

	pulseIntervalRef.current = setInterval(() => {
		weight = weight + direction * step;
		if (weight >= weightMax) {
			direction = -1;
			weight = weightMax;
		} else if (weight <= weightMin) {
			direction = 1;
			weight = weightMin;
		}
		placemarkItem.setOptions({
			strokeWeight: weight
		});
	}, 50);
}

export function stopStrokePulse(pulseIntervalRef) {
	clearInterval(pulseIntervalRef.current);
	pulseIntervalRef.current = null;
}

export { DEFAULT_PLACEMARK_ITEM_STYLES };
